import {DataCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import * as net from 'util/Net';
import {callV3Internal, type V3InternalRequestBody} from 'v3-internal-client';

import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {AdapterField, InstrumentAdapter, InstrumentServiceDetails} from './ConnectionConstants';

export function connectionURL(connectionId: string): string {
  return `/connections/${connectionId}`;
}

export type CreateConnectionBody = V3InternalRequestBody<'Connection.Create'>;

export type UpdateConnectionBody = V3InternalRequestBody<'Connection.Update'>;

export function createConnection(body: CreateConnectionBody): Promise<{id: string}> {
  return callV3Internal('Connection.Create', {
    body,
    analyticsMetadata: {actionOrigin: ActionOrigin.CREATE_CONNECTIONS_MODAL},
  });
}

export function updateConnection(connectionId: string, body: UpdateConnectionBody): Promise<{id: string}> {
  return callV3Internal('Connection.Update', {
    pathParams: {connection_id: connectionId},
    body,
    analyticsMetadata: {actionOrigin: ActionOrigin.CONNECTION_PAGE},
  });
}

export function listConnections(adapterId?: string): Promise<{connections: Array<ConnectionListingGraphQL>}> {
  return net.getJSONPromise('/1/api/connections', adapterId ? {adapterId} : {});
}

export function updateConnectionOwnableInfo(
  connectionId: string,
  data: {
    collaborators: Array<DataCollaborator>;
    ownerInfo: OwnerInfo;
    owner: string;
  }
): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}/collaborators`,
    data,
  });
}

// Maximum number of items accepted by a single `Connection.BatchUpdate` request.
const BATCH_UPDATE_MAX_ITEMS = 25;

/**
 * The outcome of a batched update. Batches are applied in order and stop at the first failure, so
 * `updatedIds` holds the ids from the batches that succeeded and `error` is the failing batch's error.
 */
interface BatchUpdateResult {
  updatedIds: Array<string>;
  error?: unknown;
}

async function batchUpdateConnections(
  items: V3InternalRequestBody<'Connection.BatchUpdate'>['items']
): Promise<BatchUpdateResult> {
  const updatedIds: Array<string> = [];
  for (let i = 0; i < items.length; i += BATCH_UPDATE_MAX_ITEMS) {
    const outcome = await callV3Internal('Connection.BatchUpdate', {
      body: {items: items.slice(i, i + BATCH_UPDATE_MAX_ITEMS)},
    }).then(
      (response) => ({ids: response.items.map((item) => item.id)}),
      (error: unknown) => ({error})
    );
    if ('error' in outcome) {
      return {updatedIds, error: outcome.error};
    }
    updatedIds.push(...outcome.ids);
  }
  return {updatedIds};
}

/**
 * Archives connections. If a batch fails, resolves with the ids archived before the failure and the
 * failure's `error` rather than rejecting, so callers can still apply the partial result.
 */
export async function bulkArchive(
  ids: Array<string>,
  purpose: string
): Promise<{archivedItemIds: Array<string>; error?: unknown}> {
  const {updatedIds, error} = await batchUpdateConnections(
    ids.map((id) => ({id, archived: true, archiveReason: purpose}))
  );
  return {archivedItemIds: updatedIds, ...(error !== undefined && {error})};
}

/**
 * Unarchives connections. If a batch fails, resolves with the ids unarchived before the failure and the
 * failure's `error` rather than rejecting, so callers can still apply the partial result.
 */
export async function bulkUnarchive(
  ids: Array<string>
): Promise<{unarchivedItemIds: Array<string>; error?: unknown}> {
  const {updatedIds, error} = await batchUpdateConnections(ids.map((id) => ({id, archived: false})));
  return {unarchivedItemIds: updatedIds, ...(error !== undefined && {error})};
}

// A table export runs in the background (a large Databricks export can take up to an hour), so
// starting one returns the data frame it will fill, whose status is then polled until it finishes.
const TABLE_EXPORT_STATUS_POLL_INTERVAL_MS = 3000;
// The server reports an export that hasn't finished 2.5 hours after it started as stopped, so every
// export reaches a final status by then; this waits a little longer.
const TABLE_EXPORT_MAX_WAIT_MS = 160 * 60 * 1000;

type TableExportStatus =
  | {status: 'IN_PROGRESS'}
  | {status: 'SUCCEEDED'; datasetId: string}
  | {status: 'FAILED'; errorMessage: string};

// A table export that finished without creating a Dataset. Its message is safe to show the user.
export class TableExportFailedError extends Error {}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Whether a failed status request may succeed if repeated: the request didn't reach the server
// (status 0), or the server was briefly unavailable. Other errors, such as losing access to the data
// frame, won't change.
function isTransientRequestError(err: unknown): boolean {
  const status = (err as {status?: unknown} | null)?.status;
  return typeof status === 'number' && (status === 0 || status === 429 || status >= 500);
}

// Starts exporting a Snowflake or Databricks table through the connection into a new Benchling
// Dataset, resolving with the id of the data frame to pass to waitForTableExport.
export async function startTableExport(
  connectionId: string,
  schemaName: string,
  tableName: string,
  datasetName: string
): Promise<string> {
  const {dataFrameId}: {dataFrameId: string} = await net.postJSONPromise({
    url: `/1/api/instruments/connections/${connectionId}:start-table-export`,
    data: {schemaName, tableName, datasetName},
  });
  return dataFrameId;
}

// Waits for the table export into `dataFrameId` to finish, resolving with the created dataset's id.
export async function waitForTableExport(
  dataFrameId: string,
  {pollIntervalMs = TABLE_EXPORT_STATUS_POLL_INTERVAL_MS, maxWaitMs = TABLE_EXPORT_MAX_WAIT_MS} = {}
): Promise<{dataset_id: string}> {
  const deadline = Date.now() + maxWaitMs;
  for (;;) {
    let exportStatus: TableExportStatus | null = null;
    try {
      // Sequential by design: each check waits for the previous one.
      // oxlint-disable-next-line no-await-in-loop
      exportStatus = await net.getJSONPromise(`/1/api/instruments/table-exports/${dataFrameId}`);
    } catch (err: unknown) {
      // The export keeps running on the server, so a brief outage only delays the next check.
      if (!isTransientRequestError(err) || Date.now() >= deadline) {
        throw err;
      }
    }
    if (exportStatus?.status === 'SUCCEEDED') {
      return {dataset_id: exportStatus.datasetId};
    }
    if (exportStatus?.status === 'FAILED') {
      throw new TableExportFailedError(exportStatus.errorMessage);
    }
    if (Date.now() >= deadline) {
      throw new TableExportFailedError(
        "The export is taking longer than expected. Check the connection's folder for the dataset later."
      );
    }
    // oxlint-disable-next-line no-await-in-loop
    await sleep(pollIntervalMs);
  }
}

export function listInstrumentTypes(): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/instrument_types`);
}

export function listAdapters(
  instrumentType?: string
): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/adapters?instrumentType=${instrumentType}`);
}

export function getAdapter(
  adapterId: string,
  gatewayType?: GatewayType
): Promise<{id: string; fields: Array<AdapterField>}> {
  const data = gatewayType ? {gatewayType} : {};
  return net.getJSONPromise(`/1/api/instruments/adapters/${adapterId}`, data);
}

export function getTransformDataTypes(adapterId: string): Promise<{[id: string]: string}> {
  return net.getJSONPromise(`/1/api/instruments/transform-data-types`, {adapterId});
}

export function getInstrumentServiceDetails(connectionId: string): Promise<InstrumentServiceDetails> {
  return net.getJSONPromise(`/1/api/connections/${connectionId}/info`);
}

export function startCredentialTest(connectionId: string): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: `/1/api/connections/${connectionId}:test-credentials`,
    data: {},
  });
}
